// One appliance on its own phase of a current sensor — 801038's office AC is
// alone on phase 3 (B). Its RUNS (on → off) come from the server's
// GetApplianceRuns, or, until that is deployed, from raw rows via
// runsFromRows() below — the same rule, checked against each other on a real
// day (2026-10-12: identical runs).
//
//   run = consecutive readings of the phase at ≥ minW watts. It breaks on a
//         reading below minW, or on a silence over 5 min (the sensor was
//         offline — never invent running time nobody saw). Energy: a reading
//         lasts until the next one, a gap over 2 min counts as 1 min (the
//         rule of phase.energyKWh / GetCurrentSenseHourly).
//
// ⚠️ "Alone on the phase" is the user's statement, not something the data
// proves. The threshold is what keeps a small load sharing the phase out (in
// Jan 2026 a ~135 W load ran on 801038's phase B round the clock).

import { addDays, parseYmd, ymd } from './energyAnalysis';

const pad = (x) => String(x).padStart(2, '0');
const parseLocal = (s) => new Date(String(s).replace('Z', '').replace('T', ' '));
const stamp = (t) => `${ymd(t)} ${pad(t.getHours())}:${pad(t.getMinutes())}:${pad(t.getSeconds())}`;

/** Raw CurrentSense rows → runs, exactly as GetApplianceRuns computes them. */
export function runsFromRows(rows, phase, minW) {
  const list = (rows || [])
    .filter((r) => (Number(r.Phase ?? r.phase) || 1) === phase)
    .map((r) => ({
      t: parseLocal(r.DateTime), w: Number(r.power) || 0, a: Number(r.current) || 0, v: Number(r.voltage) || 0,
    }))
    .filter((r) => !Number.isNaN(+r.t))
    .sort((a, b) => a.t - b.t);

  const runs = [];
  let cur = null;
  list.forEach((r, i) => {
    const prev = list[i - 1];
    const next = list[i + 1];
    const on = r.w >= minW;
    const gap = next ? (next.t - r.t) / 1000 : null;
    const dt = gap == null ? 0 : (gap > 120 ? 60 : gap);
    const prevGap = prev ? (r.t - prev.t) / 1000 : null;
    if (!on) {
      cur = null;
      return;
    }
    if (!cur || !prev || prev.w < minW || prevGap > 300) {
      cur = {
        s: r.t, e: r.t, n: 0, wh: 0, sumW: 0, mw: 0, sumA: 0, sumVA: 0,
      };
      runs.push(cur);
    }
    cur.n += 1;
    cur.wh += (r.w * dt) / 3600;
    cur.sumW += r.w;
    cur.mw = Math.max(cur.mw, r.w);
    cur.sumA += r.a;
    cur.sumVA += r.v * r.a;
    const end = new Date(+r.t + dt * 1000);
    if (end > cur.e) cur.e = end;
  });

  return runs.map((x) => ({
    s: stamp(x.s),
    e: stamp(x.e),
    n: x.n,
    wh: Math.round(x.wh * 100) / 100,
    aw: Math.round(x.sumW / x.n),
    mw: x.mw,
    aa: Math.round((x.sumA / x.n) * 100) / 100,
    pf: x.sumVA > 0 ? Math.round((x.sumW / x.sumVA) * 1000) / 1000 : null,
  }));
}

/** Join runs less than `bridgeS` apart — one dip below the threshold is not a switch-off. */
export function mergeRuns(runs, bridgeS = 120) {
  const out = [];
  (runs || []).forEach((r) => {
    const s = parseLocal(r.s);
    const e = parseLocal(r.e);
    const last = out[out.length - 1];
    if (last && (s - last.eT) / 1000 <= bridgeS) {
      const n = last.n + r.n;
      last.aw = Math.round((last.aw * last.n + r.aw * r.n) / n);
      last.aa = Math.round(((last.aa * last.n + r.aa * r.n) / n) * 100) / 100;
      last.pf = last.pf != null && r.pf != null ? (last.pf * last.n + r.pf * r.n) / n : (last.pf ?? r.pf);
      last.n = n;
      last.wh += r.wh;
      last.mw = Math.max(last.mw, r.mw);
      if (e > last.eT) { last.eT = e; last.e = r.e; }
      return;
    }
    out.push({ ...r, sT: s, eT: e });
  });
  return out;
}

const HOUR = 3600;

/** Seconds of [s, e) that fall outside working hours (or all of it on an off day). */
function offHoursSeconds(s, e, settings) {
  let total = 0;
  for (let t = new Date(s); t < e;) {
    const dayEnd = addDays(new Date(t.getFullYear(), t.getMonth(), t.getDate()), 1);
    const segEnd = e < dayEnd ? e : dayEnd;
    const day = new Date(t.getFullYear(), t.getMonth(), t.getDate());
    if ((settings.offDays || []).includes(day.getDay())) {
      total += (segEnd - t) / 1000;
    } else {
      const ws = new Date(+day + settings.workStart * HOUR * 1000);
      const we = new Date(+day + settings.workEnd * HOUR * 1000);
      const inside = Math.max(0, (Math.min(segEnd, we) - Math.max(t, ws)) / 1000);
      total += (segEnd - t) / 1000 - inside;
    }
    t = segEnd;
  }
  return total;
}

/**
 * runs (merged) in [from, to) → the appliance report.
 * -> { daily: [{ d, type, hours, kwh, starts, first, last, avgW }], totals, alerts, weekly, trendPct }
 */
export function applianceReport(runs, settings, from, to, now = new Date()) {
  const byDay = new Map();
  for (let dt = new Date(from); dt < to && dt <= now; dt = addDays(dt, 1)) {
    const d = ymd(dt);
    byDay.set(d, {
      d,
      type: (settings.offDays || []).includes(dt.getDay()) ? 'off' : 'work',
      hours: 0,
      kwh: 0,
      starts: 0,
      first: null,
      last: null,
      sumW: 0,
      n: 0,
    });
  }

  const alerts = [];
  runs.forEach((r) => {
    const d = ymd(r.sT);
    const day = byDay.get(d);
    if (!day) return;
    const secs = (r.eT - r.sT) / 1000;
    day.hours += secs / HOUR;
    day.kwh += r.wh / 1000;
    day.starts += 1;
    day.sumW += r.aw * r.n;
    day.n += r.n;
    if (!day.first || r.sT < day.first) day.first = r.sT;
    if (!day.last || r.eT > day.last) day.last = r.eT;
    const off = offHoursSeconds(r.sT, r.eT, settings);
    if (off >= 15 * 60) {
      alerts.push({
        d,
        offDay: day.type === 'off',
        s: r.sT,
        e: r.eT,
        offSeconds: off,
        kwh: (r.wh / 1000) * (off / Math.max(1, secs)),
      });
    }
  });

  const daily = [...byDay.values()].map(({ sumW, n, ...x }) => ({ ...x, avgW: n ? Math.round(sumW / n) : 0 }));

  // Running watts week by week — a steady climb is the maintenance hint
  // (dirty filter / coil, low gas) once the weather is accounted for.
  const weekly = [];
  for (let i = 0; i < daily.length; i += 7) {
    const wk = daily.slice(i, i + 7);
    const h = wk.reduce((a, x) => a + x.hours, 0);
    const k = wk.reduce((a, x) => a + x.kwh, 0);
    if (h >= 2) weekly.push({ from: wk[0].d, avgW: Math.round((k * 1000) / h), hours: h });
  }
  const trendPct = weekly.length >= 2
    ? Math.round(((weekly[weekly.length - 1].avgW - weekly[0].avgW) / weekly[0].avgW) * 100) : null;

  const used = daily.filter((x) => x.hours > 0);
  const work = daily.filter((x) => x.type === 'work' && x.hours > 0);
  const totals = {
    kwh: daily.reduce((a, x) => a + x.kwh, 0),
    hours: daily.reduce((a, x) => a + x.hours, 0),
    daysUsed: used.length,
    avgKwhWorkDay: work.length ? work.reduce((a, x) => a + x.kwh, 0) / work.length : 0,
    avgHoursWorkDay: work.length ? work.reduce((a, x) => a + x.hours, 0) / work.length : 0,
    avgW: (() => {
      const h = daily.reduce((a, x) => a + x.hours, 0);
      return h ? Math.round((daily.reduce((a, x) => a + x.kwh, 0) * 1000) / h) : 0;
    })(),
    maxW: runs.reduce((a, r) => Math.max(a, r.mw), 0),
    pf: (() => {
      const xs = runs.filter((r) => r.pf != null);
      const n = xs.reduce((a, r) => a + r.n, 0);
      return n ? xs.reduce((a, r) => a + r.pf * r.n, 0) / n : null;
    })(),
    medianOn: medianClock(used.map((x) => x.first)),
    medianOff: medianClock(used.map((x) => x.last)),
  };

  return {
    daily, totals, alerts, weekly, trendPct,
  };
}

/** The typical clock time of a list of Dates, as 'HH:MM'. */
function medianClock(times) {
  const mins = times.filter(Boolean).map((t) => t.getHours() * 60 + t.getMinutes()).sort((a, b) => a - b);
  if (!mins.length) return null;
  const m = mins[mins.length >> 1];
  return `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
}

export const clock = (t) => (t ? `${pad(t.getHours())}:${pad(t.getMinutes())}` : '—');
export { parseYmd };
